// Iter 82.4 / Phase E2.0 — Shift Assignments admin.
// Versioned, date-effective binding of employee → shift. This page is
// the operator surface for the most safety-critical concept in E2: a
// later shift change MUST NEVER recompute historical attendance.
// New assignments auto-terminate the previous active one one day
// before their effective_from. Future assignments can be deleted; past
// or active ones can only be superseded via a new assignment.
import { useCallback, useEffect, useState } from "react";
import { Plus, X, FloppyDisk, Clock, ArrowsClockwise, Trash, Buildings } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AttendanceAssignments() {
    const { can } = useAuth();
    const [shifts, setShifts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [activeOnly, setActiveOnly] = useState(true);
    const [employeeFilter, setEmployeeFilter] = useState("");
    const [shiftFilter, setShiftFilter] = useState("");

    // Assign dialog
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ employee_id: "", shift_id: "", effective_from: todayIso(), reason: "manual_assignment", remarks: "" });

    // History dialog
    const [historyEmp, setHistoryEmp] = useState(null);
    const [history, setHistory] = useState([]);

    // Resolve dialog
    const [resolveEmp, setResolveEmp] = useState(null);
    const [resolveDate, setResolveDate] = useState(todayIso());
    const [resolveResult, setResolveResult] = useState(null);

    const reload = useCallback(async () => {
        try {
            const [s, e] = await Promise.all([
                api.get("/attendance/shift/list"),
                api.get("/hr/employee/list", { params: { limit: 500 } }),
            ]);
            setShifts(s.data.items || []);
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reference data"));
        }
    }, []);

    const reloadRows = useCallback(async () => {
        try {
            const params = { active_only: activeOnly };
            if (employeeFilter) params.employee_id = employeeFilter;
            if (shiftFilter) params.shift_id = shiftFilter;
            const { data } = await api.get("/attendance/assignment/list", { params });
            setRows(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load assignments"));
        }
    }, [activeOnly, employeeFilter, shiftFilter]);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);
    useEffect(() => { Promise.resolve().then(() => { reloadRows(); }); }, [reloadRows]);

    const empById = Object.fromEntries(employees.map((e) => [e.id, e]));
    const shiftById = Object.fromEntries(shifts.map((s) => [s.id, s]));

    const openAssign = (employeeId) => {
        setDraft({ employee_id: employeeId || "", shift_id: "", effective_from: todayIso(), reason: "manual_assignment", remarks: "" });
        setOpen(true);
    };

    const save = async () => {
        if (!draft.employee_id || !draft.shift_id || !draft.effective_from) {
            toast.error("Employee, shift, and effective date are all required.");
            return;
        }
        try {
            await api.post("/attendance/assignment", draft);
            toast.success("Assigned.");
            setOpen(false);
            reloadRows();
        } catch (err) {
            toast.error(formatApiError(err, "Assignment failed"));
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Hard-delete assignment ${row.id}? Only allowed when effective_from is still in the future.`)) return;
        try {
            await api.delete(`/attendance/assignment/${row.id}`);
            toast.success("Assignment deleted.");
            reloadRows();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const openHistory = async (emp) => {
        setHistoryEmp(emp);
        try {
            const { data } = await api.get(`/attendance/assignment/history/${emp.id}`);
            setHistory(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load history"));
        }
    };

    const runResolve = async () => {
        if (!resolveEmp) return;
        try {
            const { data } = await api.get("/attendance/assignment/resolve", {
                params: { employee_id: resolveEmp.id, as_of_date: resolveDate },
            });
            setResolveResult(data);
        } catch (err) {
            toast.error(formatApiError(err, "Resolution failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="attendance-assignments-page">
            <PageHeader
                eyebrow="Attendance Configuration · E2.0"
                title="Shift Assignments"
                description="Versioned, date-effective bindings of employees to shifts. The historical assignment in effect on a given date is the authoritative source for E2.1 attendance computation — a later shift change never rewrites the past."
                actions={can("employee:admin") && (
                    <Button onClick={() => openAssign("")} data-testid="assign-new-btn"><Plus size={14} className="mr-1" /> New Assignment</Button>
                )}
            />

            {/* Filters */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end" data-testid="assignments-filters">
                <div className="min-w-[200px]">
                    <Label className="text-xs">Employee</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} data-testid="filter-employee">
                        <option value="">All employees</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</option>)}
                    </select>
                </div>
                <div className="min-w-[200px]">
                    <Label className="text-xs">Shift</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)} data-testid="filter-shift">
                        <option value="">All shifts</option>
                        {shifts.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
                    </select>
                </div>
                <label className="flex items-center gap-2 text-xs h-10">
                    <input type="checkbox" data-testid="filter-active-only" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                    Show active only (effective_to=null)
                </label>
            </div>

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="assignments-section">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Assignment ID</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Shift</th>
                            <th className="text-left px-3 py-2 font-medium">Effective Window</th>
                            <th className="text-left px-3 py-2 font-medium">Reason</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="w-44" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-slate-400">No assignments found for the current filters.</td></tr>
                        )}
                        {rows.map((r) => {
                            const emp = empById[r.employee_id];
                            const sh = shiftById[r.shift_id];
                            const isFuture = r.effective_from > todayIso();
                            const isActive = !r.effective_to;
                            return (
                                <tr key={r.id} className="border-t border-slate-100" data-testid={`assignment-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.id}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-slate-700">{emp?.full_name || r.employee_id}</div>
                                        <div className="text-[10px] text-slate-400 font-mono">{emp?.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-slate-700">
                                        {sh ? <>{sh.name} <span className="text-slate-400 text-[10px]">({sh.start_time}–{sh.end_time})</span></> : r.shift_id}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600">
                                        {formatDate(r.effective_from)} → {r.effective_to ? formatDate(r.effective_to) : <span className="text-emerald-600">Active</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{r.reason}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {isFuture && <Badge className="bg-sky-100 text-sky-700">Scheduled</Badge>}
                                        {isActive && !isFuture && <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                                        {!isActive && <Badge variant="secondary">Terminated</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs flex justify-end gap-1">
                                        {emp && (
                                            <button onClick={() => openHistory(emp)} className="text-slate-500 hover:text-slate-700 px-2" title="History" data-testid={`assignment-history-${r.id}`}>
                                                <Clock size={14} />
                                            </button>
                                        )}
                                        {emp && (
                                            <button onClick={() => { setResolveEmp(emp); setResolveDate(todayIso()); setResolveResult(null); }} className="text-slate-500 hover:text-slate-700 px-2" title="Resolve as-of date" data-testid={`assignment-resolve-${r.id}`}>
                                                <ArrowsClockwise size={14} />
                                            </button>
                                        )}
                                        {can("employee:admin") && isFuture && (
                                            <button onClick={() => remove(r)} className="text-rose-500 hover:text-rose-700 px-2" title="Delete future assignment" data-testid={`assignment-delete-${r.id}`}>
                                                <Trash size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            {/* Assign dialog */}
            <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
                <DialogContent data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle>New Shift Assignment</DialogTitle>
                        <DialogDescription>The previous active assignment will auto-terminate one day before the new effective date. Future overlaps must be deleted manually first.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Employee</Label>
                            <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={draft.employee_id} onChange={(e) => setDraft({ ...draft, employee_id: e.target.value })} data-testid="assign-employee">
                                <option value="">— select —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.employee_code} · {e.department || "—"}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Shift</Label>
                            <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={draft.shift_id} onChange={(e) => setDraft({ ...draft, shift_id: e.target.value })} data-testid="assign-shift">
                                <option value="">— select —</option>
                                {shifts.filter((s) => s.is_active !== false).map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name} · {s.start_time}–{s.end_time}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Effective From</Label>
                            <Input type="date" data-testid="assign-effective-from" value={draft.effective_from} onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Reason</Label>
                            <Input data-testid="assign-reason" value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} placeholder="manual_assignment / role_change / ..." />
                        </div>
                        <div>
                            <Label className="text-xs">Remarks</Label>
                            <Textarea rows={2} data-testid="assign-remarks" value={draft.remarks} onChange={(e) => setDraft({ ...draft, remarks: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="assign-save"><FloppyDisk size={14} className="mr-1" /> Assign</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History dialog */}
            <Dialog open={!!historyEmp} onOpenChange={(o) => { if (!o) { setHistoryEmp(null); setHistory([]); } }}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
                    <DialogHeader>
                        <DialogTitle>Assignment History — {historyEmp?.full_name}</DialogTitle>
                        <DialogDescription>Full lineage of shift assignments for {historyEmp?.employee_code}. Each terminated row carries its supersession reference.</DialogDescription>
                    </DialogHeader>
                    <table className="w-full text-sm mt-2">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">From</th>
                                <th className="text-left px-3 py-2 font-medium">To</th>
                                <th className="text-left px-3 py-2 font-medium">Shift</th>
                                <th className="text-left px-3 py-2 font-medium">Reason / Termination</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No history yet.</td></tr>}
                            {history.map((h) => (
                                <tr key={h.id} className="border-t border-slate-100">
                                    <td className="px-3 py-2 text-xs">{formatDate(h.effective_from)}</td>
                                    <td className="px-3 py-2 text-xs">{h.effective_to ? formatDate(h.effective_to) : <span className="text-emerald-600">Active</span>}</td>
                                    <td className="px-3 py-2 text-xs text-slate-700">{h.shift ? <>{h.shift.name} <span className="text-slate-400">({h.shift.start_time}–{h.shift.end_time})</span></> : h.shift_id}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">
                                        <div>{h.reason}</div>
                                        {h.terminated_reason && <div className="text-[10px] text-slate-400 mt-0.5">{h.terminated_reason}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DialogContent>
            </Dialog>

            {/* As-of-date resolve dialog */}
            <Dialog open={!!resolveEmp} onOpenChange={(o) => { if (!o) { setResolveEmp(null); setResolveResult(null); } }}>
                <DialogContent data-testid="resolve-dialog">
                    <DialogHeader>
                        <DialogTitle>Resolve Shift — {resolveEmp?.full_name}</DialogTitle>
                        <DialogDescription>Query the historically-correct shift assignment for a given date. Used to verify E2.1 attendance computations.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Label className="text-xs">As of date</Label>
                                <Input type="date" data-testid="resolve-date" value={resolveDate} onChange={(e) => setResolveDate(e.target.value)} />
                            </div>
                            <Button onClick={runResolve} data-testid="resolve-run-btn">Resolve</Button>
                        </div>
                        {resolveResult && (
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1" data-testid="resolve-result">
                                {resolveResult.shift ? (
                                    <>
                                        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                            <Buildings size={14} /> {resolveResult.shift.name}
                                            <Badge variant="outline" className="text-[10px]">{resolveResult.shift.code}</Badge>
                                        </div>
                                        <div className="text-xs text-slate-600">Window: {resolveResult.shift.start_time} → {resolveResult.shift.end_time}{resolveResult.shift.is_overnight && " (overnight)"}</div>
                                        <div className="text-xs text-slate-600">Assignment effective: {formatDate(resolveResult.assignment.effective_from)} → {resolveResult.assignment.effective_to ? formatDate(resolveResult.assignment.effective_to) : "(open)"}</div>
                                    </>
                                ) : (
                                    <div className="text-xs text-rose-600">{resolveResult.reason}</div>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
